import { DestroyRef, Injectable, computed, effect, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, take } from "rxjs/operators";
import { of, Subscription } from "rxjs";

import { environment } from "@environments/environment";

import { CopilotApiService } from "@api/api/copilot.service";
import { CopilotChatEvent } from "@api/model/copilotChatEvent";

import { permissionsAllow } from "../../core/models/user.model";
import { AuthService } from "../../core/services/auth.service";
import { LoggerService } from "../../core/services/logger.service";
import {
  AguiEvent,
  AguiEventSchema,
  HouseEnvelopeSchema,
} from "./agent-chat.schemas";
import {
  ClientToolRegistry,
  ClientToolResult,
  DraftChangeRequest,
  isCopilotFileableType,
} from "./client-tool-registry.service";
import { ToolOutcome, toolResultView } from "./tool-result";
import { CatalogEntry } from "./agent-component-catalog.service";
import { CopilotStatusService } from "./copilot-status.service";
import {
  InvestigateContext,
  buildInvestigatePrompt,
} from "./investigate-context";

/**
 * Interactive-copilot AG-UI client (M1, read-only chat).
 *
 * Opens a dedicated WebSocket to the `/ws/copilot` relay, sends one operator
 * turn as the §4 house envelope `{type:"agui.run",data:{message,threadId}}`, and
 * folds the streamed AG-UI events (RUN_STARTED, TEXT_MESSAGE_*, TOOL_CALL_*,
 * RUN_FINISHED, RUN_ERROR) into a signal-based transcript the panel renders. Each
 * inbound frame is the house envelope `{type:"agui.event",data:{runId,seq,event}}`
 * (or `{type:"agui.error",data:{code,message}}`); `data.seq` is monotonic per run
 * so a reconnect repaint (via the REST history endpoint) can splice cleanly.
 * Every inbound frame is Zod-validated ({@link HouseEnvelopeSchema}) at the single
 * `JSON.parse` boundary — an invalid frame is logged and dropped, never applied.
 *
 * Deliberately NOT built on the generic WebSocketService — that service's
 * message contract is a fixed typed enum; AG-UI needs an open event stream. This
 * is a focused, self-contained feature socket using the same JWT-subprotocol
 * upgrade the platform uses everywhere (`new WebSocket(url, [token])`).
 */

interface RunScope {
  readonly generation: number;
  readonly principal: string | null;
}

export type ChatStatus = "idle" | "connecting" | "running" | "done" | "error";

export interface ToolCallView {
  readonly id: string;
  name: string;
  args: string;
  status: ToolOutcome;
  failureReason?: string;
  result?: string;
  /** §18 evidence trail — relay-stamped per-call latency (absent on pre-stamp rows). */
  latencyMs?: number;
  /** §18 evidence trail — relay best-effort row count (absent when ambiguous). */
  rowCount?: number;
  /** §18 provenance — relay stamp origin (e.g. "gateway") + RFC3339 timestamp. */
  source?: string;
  stampedAt?: string;
}

/** §18 run-cost footer — the last finished run's stats (relay + sidecar sourced). */
export interface RunStats {
  readonly tokensIn?: number;
  readonly tokensOut?: number;
  readonly toolCallCount?: number;
  /** Relay-stamped wall time for the run (razorMeta.wallMs on RUN_FINISHED). */
  readonly wallMs?: number;
}

/** M3 — the operator's decision on an agent-drafted change request. */
export type DraftDecision = "pending" | "approved" | "rejected" | "expired";

/** M3 — a draft-CR review card in the transcript (the HITL affordance). */
export interface DraftView extends DraftChangeRequest {
  status: DraftDecision;
  /** Immutable provenance captured when this card was issued, never the current turn. */
  readonly originRunId?: string;
  readonly originToolCallId?: string;
  decisionNote?: string;
  /** §18 one-click filing — stable idempotency key minted at card creation. */
  readonly idempotencyKey?: string;
  /** True while `POST /copilot/change-requests` is in flight for this card. */
  filing?: boolean;
  /** The created PENDING proposal's id once filing succeeded. */
  filedProposalId?: string;
  /** Operator-facing filing failure (fallback: copy the draft instead). */
  filingError?: string;
}

/** M2-1 — a block of agent-rendered generative-UI components in the transcript. */
export interface ComponentsView {
  readonly entries: readonly CatalogEntry[];
  /**
   * GAP-CP-021-B — the last RELAY-STAMPED gateway tool that finished before
   * this block rendered (best-effort provenance for the "rendered from …"
   * chip). Null when no stamped tool result preceded the block in this run.
   */
  readonly sourceTool: string | null;
}

export type ChatItem =
  | { readonly kind: "user"; readonly id: string; text: string }
  | { readonly kind: "assistant"; readonly id: string; text: string }
  | { readonly kind: "tool"; readonly id: string; tool: ToolCallView }
  | { readonly kind: "draft"; readonly id: string; draft: DraftView }
  | { readonly kind: "warning"; readonly id: string; readonly text: string }
  | {
      readonly kind: "components";
      readonly id: string;
      components: ComponentsView;
    };

/**
 * GAP-CP-005-B — the single capability the backend copilot gates require.
 * Mirrors `operator_can_start_copilot_run` in
 * crates/razor_app/src/websocket/axum_handlers/copilot.rs, which is the same
 * exact capability required by the owner-scoped REST history endpoints.
 */
export const COPILOT_REQUIRED_PERMISSION = "copilot:chat:read";

/**
 * Whether a user is authorized to use the copilot: holds the
 * `copilot:chat:read` permission, exactly or via a backend-maintained wildcard
 * (`*:*`, `copilot:*`, `*:read` — see {@link permissionsAllow}). PERMISSION-ONLY:
 * role names never bypass this decision, mirroring the backend WS gate
 * `operator_can_start_copilot_run` → `permissions_allow(...)` → wildcard-aware
 * `PermissionCheck::check_required`. Admin-family users are still authorized in
 * practice because the seeded role grants carry the permission into their JWT
 * snapshot (GAP-CP-005-A) — the FE asserts on the PERMISSION, not the role, so
 * visibility can never disagree with the relay's 403. Pure and DI-free so
 * lightweight render gates (the per-row "Investigate with Copilot" affordances)
 * can share the exact same decision without pulling in the heavy copilot
 * service graph. UX gate only — the backend enforces the same rule
 * independently, so a tampered client cannot bypass it.
 */
export function isCopilotAuthorized(
  user: { readonly permissions?: string[] } | null,
): boolean {
  if (!user) {
    return false;
  }
  return permissionsAllow(user.permissions ?? [], COPILOT_REQUIRED_PERMISSION);
}

@Injectable({ providedIn: "root" })
export class AgentChatService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly clientTools = inject(ClientToolRegistry);
  private readonly copilotStatus = inject(CopilotStatusService);
  /** GAP-CP-021-B — the GENERATED client for every copilot REST call
   * (change-request filing AND the reconnect history repaint); the hand-rolled
   * URLs + wire-shape interface copies are gone, so backend schema drift now
   * fails the build instead of silently desyncing. */
  private readonly copilotApi = inject(CopilotApiService);

  private socket: WebSocket | null = null;
  private socketTokenSubscription: Subscription | null = null;
  // Leave time for connection setup after the token has been selected.
  private static readonly SOCKET_TOKEN_MIN_VALIDITY_MS = 30_000;
  private runGeneration = 0;
  private transcriptGeneration = 0;
  private historySubscription: Subscription | null = null;
  private filingSubscriptions = new Subscription();
  private threadId: string | null = null;
  /** Last seq applied — the reconnect-repaint cursor. */
  private lastSeq = 0;
  /**
   * Consecutive live re-attach attempts that received no frame — the loop guard.
   * Reset to 0 on a fresh turn ({@link send}) and whenever an inbound frame proves
   * the connection is healthy, so it only bounds the tight open→close-with-no-frame
   * failure loop, not legitimate recovery across a long run.
   */
  private reattachAttempts = 0;
  /** Max live re-attach attempts before falling back to the REST repaint. */
  private static readonly MAX_REATTACH_ATTEMPTS = 3;
  /**
   * REST history may still grow while the relay drains a detached run. This
   * covers the production 120s run wall-clock plus relay finalization headroom.
   */
  private static readonly MAX_HISTORY_REPAINT_ATTEMPTS = 130;
  private static readonly HISTORY_REPAINT_DELAY_MS = 1_000;
  private static readonly MAX_TOOL_CALL_ARGS_BYTES = 64 * 1024;
  private static readonly TOOL_ARGS_TRUNCATED_MARKER =
    "\n[tool arguments truncated]";
  private historyRepaintTimer: ReturnType<typeof setTimeout> | null = null;
  /** GAP-CP-021-A — the text of the turn currently connecting/running, kept so
   * a pre-first-frame connection failure can offer Retry without data loss. */
  private pendingTurnText: string | null = null;
  /** GAP-CP-021-B — name of the last relay-stamped (gateway-sourced) tool that
   * finished in this run; feeds the components block's provenance chip. */
  private lastGatewayToolName: string | null = null;
  private idCounter = 0;
  /** M2: client-tool calls in flight (toolCallId → {name, streamed args}). */
  private readonly pendingClientTools = new Map<
    string,
    { name: string; args: string; argsBytes: number; overflowed: boolean }
  >();
  /**
   * M3: draft-CR review cards awaiting the operator's Approve/Reject, keyed by
   * the draft card id. The stored resolver settles the promise the client-tool
   * `draft_change_request` is awaiting, so the agent turn's tool result reflects
   * the operator's decision.
   */
  private readonly draftResolvers = new Map<
    string,
    (decision: ClientToolResult) => void
  >();
  /** Draft card id keyed by the AG-UI tool call that is waiting for it. */
  private readonly draftIdByToolCall = new Map<string, string>();
  /** Synchronous presenter context while a client tool begins execution. */
  private presentingClientToolCallId: string | null = null;

  /** Tool name of the M3 human-in-the-loop draft affordance. */
  private static readonly DRAFT_TOOL = "draft_change_request";
  /** M2-1 generative-UI tool — renders its own components block. */
  private static readonly COMPONENTS_TOOL = "show_components";
  /**
   * Tools that render their OWN rich transcript surface (a draft card / a
   * components block), so the generic "tool" row is suppressed for them.
   */
  private static readonly SELF_RENDERING_TOOLS = new Set<string>([
    AgentChatService.DRAFT_TOOL,
    AgentChatService.COMPONENTS_TOOL,
  ]);

  /**
   * GAP-CP-021-A · the session principal (user id AND tenant id) the transcript
   * belongs to. EXACT mirror of `CopilotStatusService.authenticatedPrincipalId`
   * — keep the two in lockstep. Null while unauthenticated or before RBAC
   * hydration settles (authReady).
   */
  private readonly authenticatedPrincipalId = computed(() => {
    if (!this.auth.isAuthenticated() || !this.auth.authReady()) {
      return null;
    }
    const user = this.auth.currentUser();
    if (!user?.id) {
      return null;
    }
    return `${user.id}::${user.tenantId ?? ""}`;
  });
  /** Previous principal observed by the reset effect (sentinel ≠ null so the
   * very first evaluation records the initial principal WITHOUT resetting). */
  private lastPrincipalId: string | null | undefined = undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.reset());
    // Wire the HITL bridge: the registry's `draft_change_request` tool renders
    // into this service's transcript and awaits the operator's decision.
    this.clientTools.setDraftPresenter((draft) => this.presentDraft(draft));
    // Wire the generative-UI bridge: `show_components` renders its validated
    // entries as a components block in this service's transcript.
    this.clientTools.setComponentsPresenter((entries) =>
      this.presentComponents(entries),
    );
    // GAP-CP-021-A — SECURITY: the transcript, threadId, seeded drafts and
    // pending HITL cards are PRINCIPAL-SCOPED state. On ANY principal
    // transition (logout, login as someone else, tenant switch) wipe it all
    // and close the panel, so the next operator at this browser can never read
    // the previous operator's trading answers or continue their thread. The
    // relay enforces ownership server-side; this closes the in-browser
    // disclosure window.
    effect(() => {
      const principal = this.authenticatedPrincipalId();
      if (this.lastPrincipalId === undefined) {
        this.lastPrincipalId = principal;
        return;
      }
      if (principal === this.lastPrincipalId) {
        return;
      }
      this.lastPrincipalId = principal;
      this.reset();
      this.closePanel();
    });
  }

  private readonly itemsSig = signal<readonly ChatItem[]>([]);
  private readonly statusSig = signal<ChatStatus>("idle");
  private readonly errorSig = signal<string | null>(null);
  private readonly runIdSig = signal<string | null>(null);
  private readonly openSig = signal(false);

  /** F9·m3 — a starter prompt to pre-seed the composer with (row investigation). */
  private readonly seedSig = signal<string | null>(null);

  /** §18 context pill — the Investigate seed's entity context (dismissible). */
  private readonly investigateContextSig = signal<InvestigateContext | null>(null);
  readonly investigateContext = this.investigateContextSig.asReadonly();
  /** Dismiss the §18 context pill (the ✕). Informational only — the seeded
   * prompt (if not yet sent) stays in the composer for the operator to edit. */
  clearInvestigateContext(): void {
    this.investigateContextSig.set(null);
  }

  /** §18 run-cost footer — stats of the most recently finished run. */
  private readonly runStatsSig = signal<RunStats | null>(null);
  readonly runStats = this.runStatsSig.asReadonly();

  /** Panel visibility — the Investigate command-palette entry opens it. */
  readonly isOpen = this.openSig.asReadonly();
  openPanel(): void {
    this.openSig.set(true);
    // GAP-CP-021-A — opening the panel re-probes /copilot/status so the budget
    // footer and a COPILOT_CHAT_ENABLED flip are fresh when the operator looks.
    this.copilotStatus.refresh();
  }
  closePanel(): void {
    this.openSig.set(false);
  }

  /**
   * The latest seed prompt for the composer (null when none pending). The panel
   * watches this and pre-fills its input; it does NOT auto-send — the operator
   * reviews and sends, matching the existing composer UX.
   */
  readonly seededPrompt = this.seedSig.asReadonly();

  /**
   * F9·m3 — "Investigate with Copilot". Open the panel pre-seeded with a starter
   * prompt built from a row's context (its correlated event ids included) so the
   * operator can send it (or edit first). Reusable from any row surface (audit
   * events, alerts, …). Read-only: seeds a prompt, never mutates anything.
   */
  investigate(context: InvestigateContext): void {
    this.seedSig.set(buildInvestigatePrompt(context));
    this.investigateContextSig.set(context);
    this.openPanel();
  }

  /** Clear the pending seed once the composer has taken it (called by the panel). */
  clearSeed(): void {
    if (this.seedSig() !== null) {
      this.seedSig.set(null);
    }
  }

  readonly items = this.itemsSig.asReadonly();
  readonly status = this.statusSig.asReadonly();
  readonly error = this.errorSig.asReadonly();
  readonly currentRunId = this.runIdSig.asReadonly();
  /** True while a turn is in flight — the composer disables send. */
  readonly busy = computed(() => {
    const s = this.statusSig();
    return s === "connecting" || s === "running";
  });

  /**
   * Whether the copilot surface is available to the CURRENT user. Three gates
   * AND-ed:
   *   - `environment.features.enableCopilotChat` — the build-time HARD
   *     kill-switch (false ⇒ hard-off, and nothing is wired: see
   *     {@link provideCopilotChat}).
   *   - `CopilotStatusService.status().enabled` — the runtime operational
   *     toggle, driven by a backend env flip, fail-closed to `false`.
   *   - {@link isCopilotAuthorized}: the `copilot:chat:read` permission (exact
   *     or via `*:*` / `copilot:*` / `*:read` wildcard) — PERMISSION-ONLY, no
   *     role-name shortcut. `/copilot/status` returns the env flag for ANY
   *     authenticated user, so WITHOUT this gate the launcher + ⌘K entry +
   *     panel would surface to unauthorized users a copilot they cannot use —
   *     the WS relay 403s them. This mirrors that gate exactly so visibility
   *     matches usability.
   * A computed signal so the surface reacts to backend status AND auth changes
   * (e.g. it fails closed the instant the user logs out and roles clear).
   */
  readonly enabled = computed(() => {
    if (environment.features.enableCopilotChat !== true) {
      return false;
    }
    if (this.copilotStatus.status().enabled !== true) {
      return false;
    }
    return isCopilotAuthorized(this.auth.currentUser());
  });

  /**
   * GAP-CP-021-A · the user turn text of a run that failed before ANY frame
   * arrived (the relay was unreachable). Non-null ⇒ the panel renders a Retry
   * button beside the error; the turn text stays in the transcript so nothing
   * the operator typed is lost.
   */
  private readonly retryableTurnSig = signal<string | null>(null);
  readonly retryableTurn = this.retryableTurnSig.asReadonly();

  /** Send one operator turn. No-op if disabled or already busy. */
  send(message: string): void {
    const trimmed = message.trim();
    if (!this.enabled() || trimmed.length === 0 || this.busy()) {
      return;
    }
    this.appendItem({ kind: "user", id: this.newId(), text: trimmed });
    this.openRunSocket(trimmed);
  }

  /**
   * GAP-CP-021-A · Retry the turn that failed before reaching the relay. The
   * user bubble is already in the transcript (retained on failure), so this
   * re-opens the run socket WITHOUT appending a duplicate turn.
   */
  retryLastTurn(): void {
    const text = this.retryableTurnSig();
    if (!text || !this.enabled() || this.busy()) {
      return;
    }
    this.openRunSocket(text);
  }

  /** Open a fresh `/ws/copilot` socket and issue `agui.run` for one turn. */
  private openRunSocket(trimmed: string): void {
    this.runGeneration += 1;
    this.stopHistoryRecovery();
    this.teardownSocket();
    this.pendingClientTools.clear();
    const scope = this.captureRunScope();
    this.errorSig.set(null);
    this.retryableTurnSig.set(null);
    this.pendingTurnText = trimmed;
    this.lastGatewayToolName = null;
    this.clearHistoryRepaintTimer();
    // Fresh operator turn — reset ALL per-RUN state, not just the retry budget.
    // This turn opens a NEW server run; the cursor (lastSeq) and run id from the
    // PREVIOUS (finished) run must not leak into it. Otherwise a mid-connect drop
    // after onopen sends `agui.run` but before this run's first `agui.event`
    // self-heals them would make the drop→re-attach path target the STALE run id
    // (with the stale seq). Clearing runId to null makes that pre-first-frame drop
    // correctly defer to the REST path (which reports a clean soft error) instead
    // of attaching to the previous run. The first `agui.event` of THIS run then
    // repopulates both. PER-THREAD state (threadId, transcript, messages) is
    // deliberately preserved so multi-turn conversations continue.
    this.reattachAttempts = 0;
    this.lastSeq = 0;
    this.runIdSig.set(null);
    this.statusSig.set("connecting");

    this.withSocketToken(scope, (token) => {
      const url = `${environment.wsUrl}/copilot`;
      let socket: WebSocket;
      try {
        socket = new WebSocket(url, [token]);
      } catch (err) {
        // The relay was never reached — retain the typed turn and offer Retry.
        this.retryableTurnSig.set(trimmed);
        this.fail("could not reach the copilot relay");
        this.logger.warn("copilot: socket open threw", {
          component: "AgentChatService",
          error: String(err),
        });
        return;
      }
      this.socket = socket;

      socket.onopen = () => {
        if (!this.isCurrentRun(scope) || this.socket !== socket) {
          return;
        }
        this.statusSig.set("running");
        // §4 house envelope opening turn. `threadId` is null on a fresh thread and
        // the backend-echoed id (captured in onFrame) on every follow-up.
        // GAP-CP-021-A — `context` is a BOUNDED, UNTRUSTED page-context object
        // (current route, the Investigate selection, tenant id) so the agent
        // knows what the operator is looking at. The relay/sidecar must treat it
        // as untrusted operator-session data (prompt context, never authority).
        socket.send(
          JSON.stringify({
            type: "agui.run",
            data: {
              message: trimmed,
              threadId: this.threadId,
              context: this.buildRunContext(),
            },
          }),
        );
      };
      socket.onmessage = (ev) => {
        if (this.isCurrentRun(scope) && this.socket === socket) {
          this.onFrame(ev.data);
        }
      };
      socket.onerror = () => this.logger.warn("copilot socket error");
      socket.onclose = () => {
        if (!this.isCurrentRun(scope) || this.socket !== socket) {
          return;
        }
        this.socket = null;
        // If the run never reached a terminal event, the browser dropped mid-run.
        // The relay drains + persists server-side after our drop, so the full
        // transcript survives. PREFER a live WS re-attach (fresh /ws/copilot +
        // agui.attach) over the REST repaint; onDrop falls back to the REST history
        // endpoint only when re-attach is rejected/impossible or its retry budget is
        // spent. A clean finish already set 'done'.
        if (this.statusSig() === "running" || this.statusSig() === "connecting") {
          this.onDrop();
        }
      };
    });
  }

  /** Abort the in-flight run and reset. */
  cancel(): void {
    this.runGeneration += 1;
    this.socketTokenSubscription?.unsubscribe();
    this.socketTokenSubscription = null;
    this.stopHistoryRecovery();
    // Detach onclose BEFORE closing so an operator-initiated abort is not mistaken
    // for a mid-run drop (which would trigger a spurious repaint).
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      // If the socket is still open, tell the relay to finalize the run as
      // `cancelled` and stop the sidecar (§4 house envelope; Batch B backend adds
      // the handler). Sent BEFORE close so the frame makes it out; a failed send
      // must not block the local abort, hence the try/catch. Without this the run
      // only ends via the relay's detach-grace reaper timeout (§6.4).
      if (socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(
            JSON.stringify({
              type: "agui.cancel",
              data: { runId: this.runIdSig() },
            }),
          );
        } catch (err) {
          this.logger.warn("copilot: cancel frame send failed", {
            component: "AgentChatService",
            error: String(err),
          });
        }
      }
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }
    if (this.statusSig() === "running" || this.statusSig() === "connecting") {
      this.statusSig.set("idle");
    }
    this.expirePendingDrafts();
  }

  /** Clear the transcript and start a fresh thread. */
  reset(): void {
    this.transcriptGeneration += 1;
    this.filingSubscriptions.unsubscribe();
    this.filingSubscriptions = new Subscription();
    this.cancel();
    this.pendingClientTools.clear();
    this.seedSig.set(null);
    this.investigateContextSig.set(null);
    this.runStatsSig.set(null);
    this.retryableTurnSig.set(null);
    this.pendingTurnText = null;
    this.lastGatewayToolName = null;
    this.itemsSig.set([]);
    this.threadId = null;
    this.lastSeq = 0;
    this.reattachAttempts = 0;
    this.runIdSig.set(null);
    this.errorSig.set(null);
    this.statusSig.set("idle");
  }

  /**
   * GAP-CP-021-A · Build the BOUNDED untrusted page-context attached to
   * `agui.run`: the operator's current route, the Investigate selection (kind /
   * id / correlated event ids), and the tenant id. Every field is length-capped
   * so a pathological URL or id list can never bloat the frame; the receiving
   * side must treat the whole object as UNTRUSTED session context.
   */
  private buildRunContext(): Record<string, unknown> {
    const ctx: Record<string, unknown> = {};
    const route = this.router.url;
    if (typeof route === "string" && route.length > 0) {
      ctx["route"] = route.slice(0, 256);
    }
    const tenantId = this.auth.currentUser()?.tenantId;
    if (typeof tenantId === "string" && tenantId.length > 0) {
      ctx["tenantId"] = tenantId.slice(0, 64);
    }
    const inv = this.investigateContextSig();
    if (inv) {
      const selection: Record<string, unknown> = {
        kind: inv.kind.slice(0, 64),
      };
      if (inv.id) {
        selection["id"] = String(inv.id).slice(0, 128);
      }
      const ids = (inv.correlatedEventIds ?? [])
        .slice(0, 20)
        .map((s) => String(s).slice(0, 128))
        .filter((s) => s.length > 0);
      if (ids.length > 0) {
        selection["correlatedEventIds"] = ids;
      }
      ctx["selection"] = selection;
    }
    return ctx;
  }

  private onFrame(raw: unknown): void {
    if (typeof raw !== "string") {
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.logger.warn("copilot: non-JSON frame");
      return;
    }

    // Zod is the trust boundary: an invalid or unknown-shaped frame is logged and
    // dropped here — never applied to the transcript, never allowed to crash it.
    const result = HouseEnvelopeSchema.safeParse(parsed);
    if (!result.success) {
      this.logger.warn("copilot: dropping invalid frame", {
        component: "AgentChatService",
        issues: result.error.issues.length,
      });
      return;
    }
    const env = result.data;

    // Relay-level error (feature disabled, auth, busy, wall-clock timeout, or a
    // rejected re-attach).
    if (env.type === "agui.error") {
      const { code, message } = env.data;
      // A live re-attach the relay refused: the run is unknown/evicted/finalized-
      // and-purged (attach_failed), the attaching connection is not the run's owner
      // (attach_forbidden), or the run still has another live consumer the relay has
      // not yet noticed is dead — e.g. a half-open-TCP reconnect after laptop
      // sleep/NAT rebind (attach_rejected). In ALL three cases the server-side run is
      // still durable, so fall back to the Batch A REST repaint to recover the
      // transcript rather than dropping into a terminal error. Tear the socket down
      // first (onclose detached) so the impending close doesn't re-enter the
      // drop→re-attach path.
      if (
        code === "attach_failed" ||
        code === "attach_forbidden" ||
        code === "attach_rejected"
      ) {
        this.logger.warn(
          "copilot: re-attach rejected, falling back to REST repaint",
          {
            component: "AgentChatService",
            code,
          },
        );
        this.teardownSocket();
        this.repaintFromHistory();
        return;
      }
      this.fail(message ?? `copilot error (${code})`);
      return;
    }

    // agui.event: fold the AG-UI event in, advancing the {runId, seq} cursor.
    const { runId, seq, threadId, event } = env.data;
    // A frame arrived → this connection is healthy. Clear the live re-attach retry
    // budget so a LATER independent drop gets a fresh set of attempts; the budget
    // only guards the tight open→close-with-no-frame loop.
    this.reattachAttempts = 0;
    if (typeof seq === "number") {
      this.lastSeq = seq;
    }
    if (runId) {
      this.runIdSig.set(runId);
    }
    // Capture the backend-resolved thread id (echoed on the envelope or the
    // RUN_STARTED event) once, so follow-up turns continue the same conversation.
    if (this.threadId === null) {
      const echoed =
        threadId ?? (event.type === "RUN_STARTED" ? event.threadId : undefined);
      if (typeof echoed === "string" && echoed.length > 0) {
        this.threadId = echoed;
      }
    }
    this.applyEvent(event);
  }

  /**
   * Fold one validated AG-UI event into the transcript.
   *
   * `opts.replay` (or a `replay:true` marker the relay stamps on the event itself)
   * marks this event as a transcript RECONSTRUCTION rather than a live delivery —
   * set on the WS re-attach replay (the relay stamps `event.replay`) and on the REST
   * repaint (the call site passes `{ replay:true }`, since persisted rows carry no
   * marker). The only branch that changes on replay is TOOL_CALL_END for a client
   * tool: on replay it must NOT re-fire the browser side-effect nor re-send a result
   * (see the TOOL_CALL_END case); every other event applies identically live or on
   * replay (they only ever RECONSTRUCT the transcript, never trigger a side-effect).
   */
  private applyEvent(event: AguiEvent, opts?: { replay?: boolean }): void {
    // A frame is a replay if the relay stamped the event (WS re-attach path) OR the
    // caller flagged it (REST repaint path). Either way: reconstruct, never re-fire.
    const isReplay =
      opts?.replay === true ||
      (event.type === "TOOL_CALL_END" && event.replay === true);
    switch (event.type) {
      case "RUN_STARTED": {
        this.statusSig.set("running");
        const warningId = `history-warning-${this.runIdSig() ?? this.runGeneration}`;
        if (event.razorMeta?.historyDegraded === true &&
            !this.itemsSig().some((item) => item.id === warningId)) {
          this.appendItem({
            kind: "warning", id: warningId,
            text: "Earlier conversation history was unavailable for this answer. Restate the relevant context or start a new conversation.",
          });
        }
        break;
      }
      case "TEXT_MESSAGE_START":
        if (event.messageId) {
          // Idempotent: a fresh message creates an empty assistant bubble; a REPEAT
          // START for the same messageId RESETS its accumulated text to empty. The
          // repeat case is the mid-message live re-attach seam (§6.4 F2·m1): the
          // relay replays a START + one TEXT_MESSAGE_CONTENT carrying the FULL
          // buffered-so-far text for a still-streaming message (whose consolidated
          // row is not yet persisted, so the persisted replay misses it). Resetting
          // here lets that full snapshot REPLACE the stale pre-detach prefix — the
          // following live deltas then append cleanly, with no gap and no duplicate.
          this.startAssistant(event.messageId);
        }
        break;
      case "TEXT_MESSAGE_CONTENT":
        if (event.messageId && event.delta) {
          // A `consolidated:true` event is a WHOLE-MESSAGE snapshot (the relay's
          // coalesced flush row, or the in-flight re-attach replay) — REPLACE the
          // message's accumulated text so a re-attach or REST repaint that arrives
          // after a mid-message detach renders the FULL text, not the truncated
          // prefix the FE held. An unmarked live delta APPENDS as normal. This holds
          // for the "completes-during-detach, FE-saw-first-delta" case: incremental
          // replay(seq>lastSeq) delivers the consolidated row → full text, no dup.
          if (event.consolidated) {
            this.replaceAssistantText(event.messageId, event.delta);
          } else {
            this.appendAssistantDelta(event.messageId, event.delta);
          }
        }
        break;
      case "TEXT_MESSAGE_END":
        break;
      case "TOOL_CALL_START":
        if (event.toolCallId) {
          const name = event.toolCallName ?? "tool";
          // M2: a call to a FE-registered tool is executed in the browser, not
          // the sidecar. Buffer its streamed args to run at TOOL_CALL_END.
          if (this.clientTools.has(name)) {
            this.pendingClientTools.set(event.toolCallId, {
              name,
              args: "",
              argsBytes: 0,
              overflowed: false,
            });
          }
          // M3/M2-1: self-rendering tools (draft-CR review card, show_components
          // block) draw their own rich surface when their args finish streaming,
          // so suppress the generic tool row for them.
          if (!AgentChatService.SELF_RENDERING_TOOLS.has(name)) {
            this.appendItem({
              kind: "tool",
              id: event.toolCallId,
              tool: { id: event.toolCallId, name, args: "", status: "running" },
            });
          }
        }
        break;
      case "TOOL_CALL_ARGS":
        if (event.toolCallId && event.delta) {
          this.updateTool(event.toolCallId, (t) => {
            if (t.args.endsWith(AgentChatService.TOOL_ARGS_TRUNCATED_MARKER)) {
              return t;
            }
            const encoded = new TextEncoder().encode(t.args + event.delta);
            if (
              encoded.byteLength <= AgentChatService.MAX_TOOL_CALL_ARGS_BYTES
            ) {
              return { ...t, args: t.args + event.delta };
            }
            const markerBytes = new TextEncoder().encode(
              AgentChatService.TOOL_ARGS_TRUNCATED_MARKER,
            ).byteLength;
            const prefix = new TextDecoder().decode(
              encoded.slice(
                0,
                AgentChatService.MAX_TOOL_CALL_ARGS_BYTES - markerBytes - 3,
              ),
            );
            return {
              ...t,
              args: prefix + AgentChatService.TOOL_ARGS_TRUNCATED_MARKER,
            };
          });
          const pending = this.pendingClientTools.get(event.toolCallId);
          if (pending && !pending.overflowed) {
            const deltaBytes = new TextEncoder().encode(event.delta).byteLength;
            if (
              pending.argsBytes + deltaBytes >
              AgentChatService.MAX_TOOL_CALL_ARGS_BYTES
            ) {
              pending.args = "";
              pending.argsBytes = 0;
              pending.overflowed = true;
              break;
            }
            pending.args += event.delta;
            pending.argsBytes += deltaBytes;
          }
        }
        break;
      case "TOOL_CALL_END":
        if (event.toolCallId && this.pendingClientTools.has(event.toolCallId)) {
          if (isReplay) {
            // Replay/repaint: RECONSTRUCT the terminal tool bubble only — never
            // re-execute the browser side-effect and never re-send a result. The
            // sidecar's pending client-tool future already timed out during the
            // detach window, so a re-sent result is dropped; and re-running would
            // double-fire a side-effecting tool (re-navigate, re-render a draft).
            // Clear the pending entry so a LATER live frame for the same call can't
            // resurrect and double-run it.
            this.pendingClientTools.delete(event.toolCallId);
            this.updateTool(event.toolCallId, (t) => ({
              ...t,
              status: "unknown",
            }));
          } else {
            void this.runClientTool(event.toolCallId);
          }
        }
        break;
      case "TOOL_CALL_RESULT":
        if (event.toolCallId) {
          this.expireDraftFromToolResult(event.toolCallId, event.content);
          // §18 evidence — fold in the relay stamp (trusted: the relay strips
          // sidecar-forged razorMeta, so a present stamp is genuine). Optional:
          // pre-stamp persisted rows render without ms/rows.
          const meta = event.razorMeta;
          // GAP-CP-021-B — remember the last STAMPED (gateway-sourced) tool so
          // a following show_components block can credit its data source.
          const outcome = toolResultView(event.content, meta?.source);
          if (meta?.source === "gateway" && outcome.status === "succeeded") {
            const stamped = this.itemsSig().find(
              (i): i is Extract<ChatItem, { kind: "tool" }> =>
                i.kind === "tool" && i.id === event.toolCallId,
            );
            if (stamped) {
              this.lastGatewayToolName = stamped.tool.name;
            }
          }
          this.updateTool(event.toolCallId, (t) => ({
            ...t,
            ...outcome,
            failureReason: outcome.failureReason,
            result: event.content,
            latencyMs: meta?.latencyMs ?? t.latencyMs,
            rowCount: meta?.rowCount ?? t.rowCount,
            source: meta?.source ?? t.source,
            stampedAt: meta?.stampedAt ?? t.stampedAt,
          }));
        }
        break;
      case "RUN_FINISHED":
        this.stopHistoryRecovery();
        this.expirePendingDrafts();
        // §18 run-cost footer — token counts from the sidecar's terminal result,
        // wall time from the relay stamp. Both optional.
        this.runStatsSig.set({
          tokensIn: event.result?.tokensIn,
          tokensOut: event.result?.tokensOut,
          toolCallCount: event.result?.toolCallCount,
          wallMs: event.razorMeta?.wallMs,
        });
        this.statusSig.set("done");
        this.teardownSocket();
        // GAP-CP-021-A — a finished run moved the tenant token budget; refresh
        // the status so the footer's budget bar reflects it immediately.
        this.copilotStatus.refresh();
        break;
      case "RUN_ERROR":
        this.fail(event.message ?? "copilot run error");
        // GAP-CP-021-A — an errored run may still have consumed budget (and a
        // budget_exceeded error means the bar is stale) — refresh.
        this.copilotStatus.refresh();
        break;
      // No default: `event` is a Zod-validated closed union (an unknown event
      // type is already dropped with a warning at the onFrame trust boundary).
    }
  }

  /**
   * A mid-run socket close. PREFER a live WS re-attach (a fresh /ws/copilot socket
   * whose first frame is `agui.attach`) over the REST repaint so the operator keeps
   * a live stream; fall back to the Batch A REST repaint only once the bounded retry
   * budget is spent (guards against an infinite reconnect loop).
   */
  private onDrop(): void {
    if (this.reattachAttempts >= AgentChatService.MAX_REATTACH_ATTEMPTS) {
      this.logger.warn(
        "copilot: live re-attach budget spent, falling back to REST repaint",
        {
          component: "AgentChatService",
          attempts: this.reattachAttempts,
        },
      );
      this.repaintFromHistory();
      return;
    }
    this.reattach();
  }

  /**
   * Live WS re-attach (§6.4 F2·m1, the preferred recovery). Open a fresh copilot
   * socket and send `{type:"agui.attach",data:{runId,lastSeq}}` as the FIRST frame
   * (instead of the opening `agui.run`). The relay authenticates the owner, then
   * replays persisted events with seq>lastSeq — each folded in via {@link onFrame},
   * spliced by seq exactly like a live frame, so there is NO gap and NO duplicate at
   * the replay→live seam — and either continues streaming the in-flight run or emits
   * its persisted terminal event and closes. If the relay rejects the attach
   * (`attach_failed`/`attach_forbidden`) we fall back to the REST repaint; if the
   * socket closes again without a frame we retry (bounded) then fall back.
   *
   * With no runId captured yet (dropped before the first event) there is nothing to
   * attach to — defer to the REST path, which reports the same soft error.
   */
  private reattach(): void {
    const scope = this.captureRunScope();
    const runId = this.runIdSig();
    if (!runId) {
      this.repaintFromHistory();
      return;
    }
    this.reattachAttempts += 1;
    this.withSocketToken(scope, (token) => {
      const url = `${environment.wsUrl}/copilot`;
      let socket: WebSocket;
      try {
        socket = new WebSocket(url, [token]);
      } catch (err) {
        this.logger.warn(
          "copilot: re-attach socket open failed, falling back to REST repaint",
          {
            component: "AgentChatService",
            error: String(err),
          },
        );
        this.repaintFromHistory();
        return;
      }
      this.socket = socket;
      const attachFromSeq = this.lastSeq;
      socket.onopen = () => {
        if (!this.isCurrentRun(scope) || this.socket !== socket) {
          return;
        }
        // First frame on a reconnect is agui.attach (NOT agui.run): resume the existing
        // run from the last seq we applied, so the relay replays only what we missed.
        socket.send(
          JSON.stringify({
            type: "agui.attach",
            data: { runId, lastSeq: attachFromSeq },
          }),
        );
      };
      socket.onmessage = (ev) => {
        if (this.isCurrentRun(scope) && this.socket === socket) {
          this.onFrame(ev.data);
        }
      };
      socket.onerror = () => this.logger.warn("copilot re-attach socket error");
      socket.onclose = () => {
        if (!this.isCurrentRun(scope) || this.socket !== socket) {
          return;
        }
        this.socket = null;
        // Still unfinished → try again (bounded) or fall back to REST.
        if (this.statusSig() === "running" || this.statusSig() === "connecting") {
          this.onDrop();
        }
      };
    });
  }

  /** Renew before native WebSocket construction, preserving the run owner while waiting. */
  private withSocketToken(
    scope: RunScope,
    open: (token: string) => void,
  ): void {
    const expiresAt = this.auth.getTokenExpirationTime()?.getTime();
    const nearExpiry =
      expiresAt !== undefined &&
      expiresAt <= Date.now() + AgentChatService.SOCKET_TOKEN_MIN_VALIDITY_MS;
    const token = nearExpiry
      ? this.auth.refreshTokenSingleFlight()
      : this.auth.ensureAccessToken();
    let receivedToken = false;
    this.socketTokenSubscription = token.pipe(take(1)).subscribe({
      next: (accessToken) => {
        receivedToken = true;
        if (!this.isCurrentRun(scope)) {
          return;
        }
        if (!accessToken || !this.enabled()) {
          this.retryableTurnSig.set(this.pendingTurnText);
          this.fail(
            "Copilot is unavailable for your current session. Sign in again to retry.",
          );
          return;
        }
        open(accessToken);
      },
      error: () => {
        if (this.isCurrentRun(scope)) {
          this.retryableTurnSig.set(this.pendingTurnText);
          this.fail(
            "Could not renew your session for Copilot. Sign in again to retry.",
          );
        }
      },
      complete: () => {
        // Central auth silently disposes an obsolete renewal. A same-user
        // reauthentication keeps the principal ID, so settle that waiting run.
        if (!receivedToken && this.isCurrentRun(scope)) {
          this.retryableTurnSig.set(this.pendingTurnText);
          this.fail(
            "Your session changed before Copilot connected. Retry to continue.",
          );
        }
      },
    });
  }

  /**
   * Close the current socket WITHOUT triggering the drop → re-attach path (detaches
   * onclose first). Used when we deliberately abandon a socket — e.g. a rejected
   * re-attach — and want the REST repaint to take over instead of another reconnect.
   */
  private teardownSocket(): void {
    this.socketTokenSubscription?.unsubscribe();
    this.socketTokenSubscription = null;
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close();
      } catch {
        // Closing an already-closing/closed socket can throw in some browsers; the
        // recovery is already in motion, so ignore.
      }
    }
  }

  /**
   * Reconnect repaint (§6.4). The browser dropped mid-run; the relay keeps
   * draining the sidecar and persisting events server-side, so the FULL transcript
   * is durable. Recover the events we missed via the REST history endpoint
   * (owner-scoped; `afterSeq` = the last cursor we applied live) and splice them in
   * by seq, then settle to the run's terminal state. The relay can still be
   * draining when a half-open socket's re-attach is rejected, so a non-terminal
   * snapshot is polled for up to the run wall-clock instead of being falsely
   * reported as a failed run. Network/5xx failures share that bounded retry
   * budget; non-retryable owner/auth/not-found responses fail immediately.
   */
  private repaintFromHistory(): void {
    const runId = this.runIdSig();
    if (!runId) {
      // GAP-CP-021-A — no run id was ever captured: NO frame arrived, so the
      // relay was never reached (or dropped us pre-RUN_STARTED). This is a
      // CONNECTION failure, not a mid-run drop: say so honestly, retain the
      // typed turn in the transcript, and offer Retry.
      this.retryableTurnSig.set(this.pendingTurnText);
      this.fail("could not reach the copilot relay");
      return;
    }
    this.clearHistoryRepaintTimer();
    this.fetchHistoryRepaint(runId, 1);
  }

  /** Fetch one incremental history snapshot and retry while the run is unfinished. */
  private fetchHistoryRepaint(runId: string, attempt: number): void {
    const scope = this.captureRunScope();
    if (this.runIdSig() !== runId || !this.isCurrentRun(scope)) {
      return;
    }
    this.historySubscription?.unsubscribe();
    this.historySubscription = this.copilotApi
      .listCopilotChatRunEvents(runId, this.lastSeq)
      .pipe(
        catchError((error: unknown) => {
          if (!this.isCurrentRun(scope)) {
            return of<CopilotChatEvent[]>([]);
          }
          const status = (error as { status?: number } | null)?.status;
          this.logger.warn("copilot: reconnect repaint fetch failed", {
            component: "AgentChatService",
            status,
          });
          if (
            status === 400 ||
            status === 401 ||
            status === 403 ||
            status === 404 ||
            status === 410
          ) {
            this.fail("copilot run history is no longer available");
          }
          return of<CopilotChatEvent[]>([]);
        }),
      )
      .subscribe((rows) => {
        if (!this.isCurrentRun(scope)) {
          return;
        }
        if (this.applyHistory(rows, runId)) {
          return;
        }
        if (attempt >= AgentChatService.MAX_HISTORY_REPAINT_ATTEMPTS) {
          this.fail("copilot connection closed before the run finished");
          return;
        }
        this.historyRepaintTimer = setTimeout(() => {
          if (!this.isCurrentRun(scope)) {
            return;
          }
          this.historyRepaintTimer = null;
          this.fetchHistoryRepaint(runId, attempt + 1);
        }, AgentChatService.HISTORY_REPAINT_DELAY_MS);
      });
  }

  /** Apply repaint rows in seq order; true means polling should stop. */
  private applyHistory(
    rows: readonly CopilotChatEvent[],
    forRunId: string,
  ): boolean {
    // Run identity guard: a stale in-flight repaint for a PREVIOUS run must never
    // splice its rows into a newly-started run/thread. If the operator reset() +
    // send()-ed a new turn (runId nulled then re-minted) while this GET was in
    // flight, the current run id no longer matches the one we fetched — drop it.
    if (this.runIdSig() !== forRunId) {
      return true;
    }
    // A run already settled to a terminal state while the fetch was in flight
    // (e.g. a late RUN_FINISHED) — don't clobber it.
    if (this.statusSig() !== "running" && this.statusSig() !== "connecting") {
      return true;
    }
    const sorted = [...rows].sort((a, b) => a.seq - b.seq);
    for (const row of sorted) {
      if (typeof row.seq === "number" && row.seq > this.lastSeq) {
        this.lastSeq = row.seq;
      }
      // CLIENT_TOOL_RESULT rows are our own FE→relay echoes, not a render event —
      // skip them (they are also absent from the inbound AguiEventSchema).
      if (row.event_type === "CLIENT_TOOL_RESULT") {
        continue;
      }
      // Validate each persisted payload the same way as a live frame.
      const parsed = AguiEventSchema.safeParse(row.payload);
      if (!parsed.success) {
        this.logger.warn("copilot: dropping invalid repaint row", {
          component: "AgentChatService",
          eventType: row.event_type,
        });
        continue;
      }
      // REST repaint rows are a transcript RECONSTRUCTION, not live delivery: flag
      // them so a persisted client-tool TOOL_CALL_END rebuilds its bubble without
      // re-firing the browser side-effect or re-sending a result (there is no open
      // socket to send on anyway — see runClientTool's readyState guard).
      this.applyEvent(parsed.data, { replay: true });
    }
    return this.statusSig() !== "running" && this.statusSig() !== "connecting";
  }

  private captureRunScope(): RunScope {
    return { generation: this.runGeneration, principal: this.authenticatedPrincipalId() };
  }

  private isCurrentRun(scope: RunScope): boolean {
    return scope.generation === this.runGeneration &&
      scope.principal === this.authenticatedPrincipalId() && this.busy();
  }

  private stopHistoryRecovery(): void {
    this.clearHistoryRepaintTimer();
    this.historySubscription?.unsubscribe();
    this.historySubscription = null;
  }

  private clearHistoryRepaintTimer(): void {
    if (this.historyRepaintTimer !== null) {
      clearTimeout(this.historyRepaintTimer);
      this.historyRepaintTimer = null;
    }
  }

  private appendItem(item: ChatItem): void {
    this.itemsSig.update((items) => [...items, item]);
  }

  /**
   * Begin (or restart) an assistant message bubble. If no bubble exists for
   * `messageId` a new empty one is appended; if one already exists its text is
   * RESET to empty (the mid-message re-attach case — see the TEXT_MESSAGE_START
   * handler). Never appends a duplicate bubble for a repeat START.
   */
  private startAssistant(messageId: string): void {
    this.itemsSig.update((items) => {
      const idx = items.findIndex(
        (i) => i.kind === "assistant" && i.id === messageId,
      );
      if (idx === -1) {
        return [...items, { kind: "assistant", id: messageId, text: "" }];
      }
      const next = [...items];
      const cur = next[idx] as Extract<ChatItem, { kind: "assistant" }>;
      next[idx] = { ...cur, text: "" };
      return next;
    });
  }

  private appendAssistantDelta(messageId: string, delta: string): void {
    this.itemsSig.update((items) => {
      const idx = items.findIndex(
        (i) => i.kind === "assistant" && i.id === messageId,
      );
      if (idx === -1) {
        return [...items, { kind: "assistant", id: messageId, text: delta }];
      }
      const next = [...items];
      const cur = next[idx] as Extract<ChatItem, { kind: "assistant" }>;
      next[idx] = { ...cur, text: cur.text + delta };
      return next;
    });
  }

  /**
   * REPLACE an assistant message's accumulated text with a whole-message snapshot
   * (a `consolidated:true` TEXT_MESSAGE_CONTENT). If no bubble exists yet one is
   * created with the full text. Used by the reconnect seam: after a mid-message
   * detach, the relay's consolidated flush row / in-flight replay carries the FULL
   * text, so replacing (not appending) heals a truncated prefix without duplicating
   * the part the FE already rendered live.
   */
  private replaceAssistantText(messageId: string, text: string): void {
    this.itemsSig.update((items) => {
      const idx = items.findIndex(
        (i) => i.kind === "assistant" && i.id === messageId,
      );
      if (idx === -1) {
        return [...items, { kind: "assistant", id: messageId, text }];
      }
      const next = [...items];
      const cur = next[idx] as Extract<ChatItem, { kind: "assistant" }>;
      next[idx] = { ...cur, text };
      return next;
    });
  }

  private updateTool(
    toolCallId: string,
    fn: (t: ToolCallView) => ToolCallView,
  ): void {
    this.itemsSig.update((items) =>
      items.map((i) =>
        i.kind === "tool" && i.id === toolCallId
          ? { ...i, tool: fn(i.tool) }
          : i,
      ),
    );
  }

  private updateDraft(id: string, fn: (d: DraftView) => DraftView): void {
    this.itemsSig.update((items) =>
      items.map((i) =>
        i.kind === "draft" && i.id === id ? { ...i, draft: fn(i.draft) } : i,
      ),
    );
  }

  /**
   * M2-1: append an agent-rendered components block to the transcript. Called by
   * the `show_components` client tool via the registry bridge after the catalog
   * atomically accepts the whole batch. The structured ack is produced by the
   * registry, not here.
   */
  private presentComponents(entries: readonly CatalogEntry[]): void {
    if (entries.length === 0) {
      return;
    }
    this.appendItem({
      kind: "components",
      id: this.newId(),
      // GAP-CP-021-B — credit the last stamped gateway tool as the data source
      // (the chip renders only when one actually finished before this block).
      components: { entries, sourceTool: this.lastGatewayToolName },
    });
  }

  /**
   * M3 HITL: render an agent-drafted change request as a review card and return a
   * promise that settles once the operator clicks Approve or Reject. The registry
   * awaits this to complete the `draft_change_request` client tool, so the
   * operator's decision is returned to the agent. A sidecar timeout expires the
   * card and resolves the local promise only for cleanup; nothing is submitted.
   */
  private presentDraft(draft: DraftChangeRequest): Promise<ClientToolResult> {
    const id = this.newId();
    const originRunId = this.runIdSig();
    this.appendItem({
      kind: "draft",
      id,
      // §18: mint the filing idempotency key at card creation so a retry after
      // a network error re-uses the SAME key (the backend 409s a true replay).
      draft: {
        ...draft, status: "pending", idempotencyKey: this.mintIdempotencyKey(),
        ...(originRunId ? { originRunId } : {}),
        ...(this.presentingClientToolCallId ? { originToolCallId: this.presentingClientToolCallId } : {}),
      },
    });
    if (this.presentingClientToolCallId) {
      this.draftIdByToolCall.set(this.presentingClientToolCallId, id);
    }
    return new Promise<ClientToolResult>((resolve) => {
      this.draftResolvers.set(id, resolve);
    });
  }

  /**
   * Operator approves the drafted CR. The decision (`{"decision":"approved"}`) is
   * returned to the agent as the `draft_change_request` tool result so it can
   * continue its turn. Nothing is submitted to any mutating endpoint — an approval
   * only surfaces the text for the operator to carry into the CR workflow.
   */
  approveDraft(id: string, note?: string): boolean {
    return this.settleDraft(id, "approved", note);
  }

  /** Reject a draft with the same optional, persisted note contract as approval. */
  rejectDraft(id: string, note?: string): boolean {
    return this.settleDraft(id, "rejected", note);
  }

  private settleDraft(id: string, status: "approved" | "rejected", note?: string): boolean {
    const resolver = this.draftResolvers.get(id);
    if (!resolver) {
      return false;
    }
    const trimmed = note?.trim();
    const decision = JSON.stringify({ decision: status, ...(trimmed ? { note: trimmed } : {}) });
    this.draftResolvers.delete(id);
    for (const [toolCallId, draftId] of this.draftIdByToolCall) {
      if (draftId === id) {
        this.draftIdByToolCall.delete(toolCallId);
        break;
      }
    }
    this.updateDraft(id, (d) => ({ ...d, status, ...(trimmed ? { decisionNote: trimmed } : {}) }));
    resolver(decision);
    return true;
  }

  /**
   * §18 one-click filing — file an APPROVED draft as a PENDING proposal in the
   * existing agent_proposals approval workflow via
   * `POST /copilot/change-requests`. OPERATOR-initiated (a human clicked File);
   * the copilot itself remains read-only, and nothing is applied — the row lands
   * `pending` and goes through the unchanged ops_approver flow. Requires an
   * agent-supplied `proposalType` + `proposalPayload` on the draft; without them
   * (free-form text) this is a no-op and the card offers copy-to-clipboard only.
   * Failures degrade to an inline error + the copy fallback.
   */
  fileDraft(id: string): void {
    const item = this.itemsSig().find(
      (i): i is Extract<ChatItem, { kind: "draft" }> =>
        i.kind === "draft" && i.id === id,
    );
    const draft = item?.draft;
    if (
      // GAP-CP-010 (FE half) — fail-closed: never file when the agent-proposals
      // feature is disabled in this build. A filed PENDING row would dead-end at
      // the 404 approvals route, so filing is only meaningful when the queue is
      // reachable. Strict `=== true` so any non-boolean override fails closed.
      environment.features.enableAgentProposals !== true ||
      !draft ||
      draft.status !== "approved" ||
      !isCopilotFileableType(draft.proposalType) ||
      !draft.originRunId ||
      !draft.proposalType ||
      !draft.proposalPayload ||
      !draft.idempotencyKey ||
      draft.filing ||
      draft.filedProposalId
    ) {
      return;
    }
    const transcriptGeneration = this.transcriptGeneration;
    const principal = this.authenticatedPrincipalId();
    this.updateDraft(id, (d) => ({ ...d, filing: true, filingError: undefined }));
    const filing = this.copilotApi
      .createCopilotChangeRequest({
        title: draft.title,
        summary: draft.summary,
        rationale: draft.rationale,
        proposalType: draft.proposalType,
        proposalPayload: draft.proposalPayload,
        runId: draft.originRunId,
        idempotencyKey: draft.idempotencyKey,
      })
      .subscribe({
        next: (created: { id?: unknown } | null) => {
          if (transcriptGeneration !== this.transcriptGeneration ||
              principal !== this.authenticatedPrincipalId()) {
            return;
          }
          // A 2xx means the pending row exists even if the body is odd — use a
          // sentinel so the card can never re-offer File after success (a
          // re-click would 409 and read as an error).
          const createdId =
            typeof created?.id === "string" && created.id.length > 0
              ? created.id
              : "filed";
          this.updateDraft(id, (d) => ({
            ...d,
            filing: false,
            filedProposalId: createdId,
          }));
        },
        error: (err: unknown) => {
          if (transcriptGeneration !== this.transcriptGeneration ||
              principal !== this.authenticatedPrincipalId()) {
            return;
          }
          const status = (err as { status?: number } | null)?.status;
          this.logger.warn("copilot: filing draft change request failed", {
            component: "AgentChatService",
            status,
          });
          this.updateDraft(id, (d) => ({
            ...d,
            filing: false,
            filingError:
              status === 400 || status === 422
                ? "This draft doesn't map to a filable proposal type — copy it instead."
                : status === 409
                  ? "Already filed (duplicate submission blocked)."
                  : "Filing failed — copy the draft and submit it manually.",
          }));
        },
      });
    this.filingSubscriptions.add(filing);
  }

  /**
   * M2: execute a FE-registered client tool and return its result to the agent.
   * The result frame goes back over the same socket; the relay forwards it to
   * the sidecar, which resumes the SDK turn with the tool output.
   */
  private async runClientTool(toolCallId: string): Promise<void> {
    const scope = this.captureRunScope();
    const socket = this.socket;
    const pending = this.pendingClientTools.get(toolCallId);
    if (!pending) {
      return;
    }
    this.pendingClientTools.delete(toolCallId);

    let content: string;
    if (pending.overflowed) {
      content = JSON.stringify({
        ok: false,
        rejected: true,
        reason: "tool_arguments_too_large",
        maxBytes: AgentChatService.MAX_TOOL_CALL_ARGS_BYTES,
      });
    } else {
      let args: Record<string, unknown> = {};
      try {
        args =
          pending.args.trim().length > 0
            ? (JSON.parse(pending.args) as Record<string, unknown>)
            : {};
      } catch {
        this.logger.warn(
          `copilot: client tool ${pending.name} had non-JSON args`,
        );
      }

      let execution: Promise<string>;
      this.presentingClientToolCallId = toolCallId;
      try {
        // ClientToolRegistry begins the selected executor synchronously, allowing
        // presentDraft() to associate its card with this AG-UI tool call.
        execution = this.clientTools.execute(pending.name, args);
      } finally {
        this.presentingClientToolCallId = null;
      }
      content = await execution;
    }
    if (!this.isCurrentRun(scope) || this.socket !== socket) {
      return;
    }
    this.updateTool(toolCallId, (t) => ({
      ...t,
      ...toolResultView(content, "client"),
      source: "client",
      result: content,
    }));

    // A sidecar timeout already became the authoritative tool result. Expiring
    // the local card resolves its browser promise only for cleanup; do not emit a
    // late CLIENT_TOOL_RESULT that the sidecar can no longer consume.
    try {
      const parsed = JSON.parse(content) as { localCancelled?: unknown };
      if (parsed.localCancelled === true) {
        return;
      }
    } catch {
      // Normal legacy string result envelope; send it upstream below.
    }

    // Send the result upstream so the agent can continue, as the §4 house
    // envelope `agui.client_tool_result`. The relay forwards it to the sidecar
    // (which resolves the pending client-tool call keyed by toolCallId and resumes
    // the SDK turn) and persists it into the transcript (event_type
    // CLIENT_TOOL_RESULT). If the socket already closed we drop it — the relay's
    // detach-grace reaper finalizes the run rather than the browser.
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: "agui.client_tool_result",
          data: { toolCallId, content },
        }),
      );
    }
  }

  private fail(message: string): void {
    this.stopHistoryRecovery();
    this.expirePendingDrafts();
    this.errorSig.set(message);
    this.statusSig.set("error");
    this.teardownSocket();
  }

  /** Close a draft when the sidecar reports that its human-wait window elapsed. */
  private expireDraftFromToolResult(
    toolCallId: string,
    content?: string,
  ): void {
    let timedOut = false;
    try {
      const parsed = JSON.parse(content ?? "") as { status?: unknown };
      timedOut = parsed.status === "timeout";
    } catch {
      return;
    }
    if (!timedOut) {
      return;
    }
    const draftId = this.draftIdByToolCall.get(toolCallId);
    if (!draftId) {
      return;
    }
    this.draftIdByToolCall.delete(toolCallId);
    const resolve = this.draftResolvers.get(draftId);
    if (!resolve) {
      return;
    }
    this.draftResolvers.delete(draftId);
    this.updateDraft(draftId, (draft) => ({ ...draft, status: "expired" }));
    resolve({
      ok: false,
      localCancelled: true,
      reason: "agent_no_longer_waiting",
    });
  }

  private expirePendingDrafts(): void {
    for (const [draftId, resolve] of this.draftResolvers) {
      this.updateDraft(draftId, (draft) =>
        draft.status === "pending" ? { ...draft, status: "expired" } : draft,
      );
      resolve({
        ok: false,
        localCancelled: true,
        reason: "run_no_longer_active",
      });
    }
    this.draftResolvers.clear();
    this.draftIdByToolCall.clear();
  }

  /** §18 — idempotency key for draft filing. Rides the same hardened id path
   * as {@link newId} (randomUUID → getRandomValues → time+counter), so a
   * non-secure/legacy context can still mint keys instead of throwing. */
  private mintIdempotencyKey(): string {
    return `cr-${this.newId()}`;
  }

  private newId(): string {
    const webCrypto = (
      globalThis as {
        crypto?: {
          randomUUID?: () => string;
          getRandomValues?: (array: Uint32Array) => Uint32Array;
        };
      }
    ).crypto;
    if (webCrypto) {
      if (typeof webCrypto.randomUUID === "function") {
        return webCrypto.randomUUID();
      }
      if (typeof webCrypto.getRandomValues === "function") {
        const random = webCrypto.getRandomValues(new Uint32Array(4));
        return `id-${Array.from(random, (word) => word.toString(36)).join("-")}`;
      }
    }
    // Last-resort uniqueness for non-secure/legacy contexts: monotonic within
    // this service instance and time-qualified across page reloads.
    this.idCounter += 1;
    return `id-${Date.now().toString(36)}-${this.idCounter.toString(36)}`;
  }
}
